// Open Questions
// - what are the use cases for startDateOverride and endDateOverride, because they don't internationalize  in the current implementation
// - can we just remove overrides entirely now that we have actual logic for "ongoing" ?
import type ExhibitionDates from 'models/ExhibitionDates';
import {
	type SupportedLocales,
	type DisplayDateExceptions,
} from '@metmuseum/types';
import { dateFormatFull, dateFormatSansYear } from 'helpers/date-time';
import { toDate } from 'date-fns-tz';

export class ExhibitionDateFormatter {
	readonly exhibitionDates: ExhibitionDates;
	readonly locale: SupportedLocales;
	private t: any; //TODO type this?

	constructor(
		exhibitionDates: ExhibitionDates,
		locale: SupportedLocales = 'en',
	) {
		this.exhibitionDates = exhibitionDates;
		this.locale = locale;
	}

	startDate(): string {
		if (this.exhibitionDates.startDateOverride)
			return this.exhibitionDates.startDateOverride; // https://i.redd.it/35almmmqen791.jpg
		const format = this.exhibitionDates.startsThisYear()
			? dateFormatSansYear
			: dateFormatFull;
		return new Intl.DateTimeFormat(this.locale, format).format(
			this.exhibitionDates.newYorkDates.start,
		);
	}

	endDate(): string {
		if (this.exhibitionDates.endDateOverride)
			return this.exhibitionDates.endDateOverride;
		return this.exhibitionDates.newYorkDates.end
			? new Intl.DateTimeFormat(this.locale, dateFormatFull).format(
					this.exhibitionDates.newYorkDates.end,
				)
			: '';
	}

	private interpolateOverrides(): string {
		return `${this.startDate()}–${this.endDate()}`;
	}

	startAndEndRange(): string {
		if (this.exhibitionDates.hasOverrides()) {
			return this.interpolateOverrides();
		}

		return new Intl.DateTimeFormat(this.locale, dateFormatFull)
			.formatRange(
				this.exhibitionDates.newYorkDates.start,
				this.exhibitionDates.newYorkDates.end,
			)
			.replaceAll(/\s(-|–|){1}\s/g, '–') // change hyphens and en-dash with spaces to all en-dash with no spaces, because style
			.replaceAll(/\s~{1}\s/g, '~'); // and I don't wanna see any spaces around those tildes either!
	}

	// What to show and when:
	// [startDateOverride || endDateOverride]     // any override is present
	// [startDate-endDate]                        // for upcoming
	// [startDate-[ongoing]]                      // for upcoming-ongoing
	// [[through | through-before-vowel]-endDate] // for current that end
	// [[ongoing]]                                // for current-ongoing
	// [startDate-endDate]                        // for current that need full range (where? EDP? - use startAndEndRange()?, not displayDates)
	// [startDate-endDate]                        // for past
	// [startDate-[ no end date]]                 // not needed?
	translate(t: any): string {
		// Overrides
		if (this.exhibitionDates.hasOverrides()) {
			return this.interpolateOverrides();
		}

		this.t = t;
		const status = this.exhibitionDates.status();
		return this[`_translate_${status}`]();
	}

	private _translate_upcoming(): string {
		if (this.exhibitionDates.neverEnds()) {
			return `${this.startDate()}-${this.t('ongoing')}`;
		}
		return this.startAndEndRange();
	}

	private _translate_current(): string {
		if (this.exhibitionDates.neverEnds()) {
			return this.t('ongoing');
		}
		// If the exhibition is current show "[through] endDate", omitting the year if it's the current year
		const format = this.exhibitionDates.endsThisYear()
			? dateFormatSansYear
			: dateFormatFull;
		const formattedEndDate = Intl.DateTimeFormat(this.locale, format).format(
			this.exhibitionDates.newYorkDates.end,
		);
		const translationKey = this.exceptions().it.endDateStartsWithVowel
			? 'throughBeforeAVowel'
			: 'through';
		return this.t(translationKey, { endDate: formattedEndDate });
	}

	private _translate_past(): string {
		return this.startAndEndRange();
	}

	private exceptions(): DisplayDateExceptions {
		// TODO: is there a better way to do this exception? all exceptions? 🤔
		// TODO: ask Smashing?? they only did the website for the EU Parliament after all
		return {
			it: {
				// Oh Madone! Don't you know before 1 and 11 "Fino al" becomes "Fino all'"?
				endDateStartsWithVowel:
					this.locale == 'it' &&
					[1, 11].includes(toDate(this.exhibitionDates.endDate).getDate()),
			},
		};
	}
}
